import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ConsignmentRequirementsClient } from "@/features/orders/ConsignmentRequirementsClient";
import { RequirementPlanningClient } from "@/features/orders/RequirementPlanningClient";
import { requirementKeys, listConsignmentRequirements, getRequirementPlanning } from "@/features/orders/orders.api";
import { firstAllowedRoute } from "@/lib/auth";

// Beheer → Vervoerseisen: the list a consignment picks from (S47), and what each asks of the vehicle and which can
// meet, for the planning (Planning v3, docs/design_handoff_planning_v3/README.md §4.10). Setting things up, so admins
// only — as with the other lists under Beheer; everyone with orders or planning reads it.

type View = "list" | "planning";

export const Route = createFileRoute("/_shell/consignment-requirements")({
  validateSearch: (search: Record<string, unknown>): { view?: View } => (search["view"] === "planning" ? { view: "planning" } : {}),
  beforeLoad: ({ context }) => {
    if (!context.session.roles.includes("admin")) {
      throw redirect({ to: firstAllowedRoute(context.session) });
    }
  },
  component: ConsignmentRequirementsScreen,
});

function ConsignmentRequirementsScreen() {
  const { t } = useTranslation();
  const { session } = Route.useRouteContext();
  const { view = "list" } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const list = useQuery({
    queryKey: requirementKeys.list("all"),
    queryFn: () => listConsignmentRequirements("all"),
    enabled: view === "list",
  });
  const planning = useQuery({
    queryKey: requirementKeys.planning(undefined),
    queryFn: () => getRequirementPlanning(),
    enabled: view === "planning",
  });

  const views: { id: View; labelKey: string }[] = [
    { id: "list", labelKey: "requirements.viewList" },
    { id: "planning", labelKey: "requirements.viewPlanning" },
  ];
  const toggle = (
    <div className="wl-segment" role="group" aria-label={t("requirements.viewLabel")}>
      {views.map((option) => (
        <button
          key={option.id}
          type="button"
          className={`wl-segment__btn${view === option.id ? " is-active" : ""}`}
          aria-pressed={view === option.id}
          onClick={() => void navigate({ search: option.id === "planning" ? { view: "planning" } : {} })}
        >
          {t(option.labelKey as never)}
        </button>
      ))}
    </div>
  );

  if (view === "planning") {
    return (
      <div className="wl-page" data-screen-label="Back-office Transport Requirements Planning">
        <RequirementPlanningClient view={planning.data} isLoading={planning.isLoading} toolbarStart={toggle} />
      </div>
    );
  }
  return (
    <div className="wl-page wl-page--list" data-screen-label="Back-office Consignment Statuses">
      <ConsignmentRequirementsClient
        initialItems={list.data ?? []}
        userId={session.userId}
        isFetching={list.isFetching}
        isLoading={list.isLoading}
        toolbarStart={toggle}
      />
    </div>
  );
}
